import React from "react";
import {
  Box,
  Typography,
  FormControl,
  TextField,
  Autocomplete,
  Select,
  MenuItem,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Controller } from "react-hook-form";

const FormSelect = ({
  label,
  name,
  control,
  errors,
  required = false,
  disabled = false,
  options = [],
  value,
  onChange,
  searchable = false,
  placeholder,
}) => {
  const rawError = errors?.[name] ?? errors;

  const fieldError =
    typeof rawError === "string"
      ? { message: rawError }
      : rawError;

  const inputStyles = {
  "& .MuiOutlinedInput-root": {
    height: "48px",
    borderRadius: "8px",
    backgroundColor: "var(--app-surface)",
    fontFamily: "Figtree",

    "& fieldset": {
      border: "1px solid var(--app-border) !important",
    },

    "&:hover fieldset": {
      border: "1px solid var(--app-border) !important",
    },

    "&.Mui-focused fieldset": {
      border: "1px solid var(--app-border) !important",
    },

    "&.Mui-focused": {
      boxShadow: "none !important",
      outline: "none !important",
    },

    "& input": {
      fontSize: "14px",
      fontWeight: 400,
      color: "var(--app-text)",
    },
  },

  /* REMOVE BLUE AUTOFILL */
  "& input:-webkit-autofill": {
    WebkitBoxShadow: "0 0 0 100px white inset !important",
    WebkitTextFillColor: "#22272B !important",
    transition: "background-color 5000s ease-in-out 0s",
  },

  "& .MuiInputBase-input::placeholder": {
    color: "var(--app-text)",
    opacity: 1,
    fontSize: "14px",
  },
};

  const dropdownStyles = {
    "& .MuiAutocomplete-paper": {
      borderRadius: "12px",
      marginTop: "6px",
      boxShadow: "var(--app-shadow)",
    },

    "& .MuiAutocomplete-listbox": {
      maxHeight: "250px",
      padding: "6px",

      "&::-webkit-scrollbar": {
        width: "6px",
      },

      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#D1D5DB",
        borderRadius: "10px",
      },
    },

    "& .MuiAutocomplete-option": {
      minHeight: "42px",
      borderRadius: "8px",
      fontSize: "14px",
      fontFamily: "Figtree",

      "&:hover": {
        backgroundColor: "var(--app-surface-2)",
      },

      '&[aria-selected="true"]': {
        backgroundColor: "var(--app-surface-2)",
        fontWeight: 400,
      },

      '&[aria-selected="true"].Mui-focused': {
        backgroundColor: "var(--app-border)",
      },
    },
  };

 const selectStyles = {
  height: "48px",
  borderRadius: "8px",
  backgroundColor: "var(--app-surface)",
  fontFamily: "Figtree",

  "& fieldset": {
    border: "1px solid var(--app-border) !important",
  },

  "&:hover fieldset": {
    border: "1px solid var(--app-border) !important",
  },

  "&.Mui-focused fieldset": {
    border: "1px solid var(--app-border) !important",
  },

  "&.Mui-focused": {
    boxShadow: "none !important",
    outline: "none !important",
  },

  "& .MuiSelect-select": {
    fontSize: "14px",
    fontWeight: 400,
    color: "var(--app-text)",
    display: "flex",
    alignItems: "center",
    height: "100% !important",
  },
};

  const menuProps = {
    PaperProps: {
      sx: {
        maxHeight: 250,
        borderRadius: "12px",
        marginTop: "6px",
        boxShadow: "var(--app-shadow)",

        "& .MuiMenuItem-root": {
          fontSize: "14px",
          minHeight: "42px",
          fontFamily: "Figtree",

          "&:hover": {
            backgroundColor: "var(--app-surface-2)",
          },

          "&.Mui-selected": {
            backgroundColor: "var(--app-surface-2)",
          },

          "&.Mui-selected:hover": {
            backgroundColor: "var(--app-border)",
          },
        },

        "&::-webkit-scrollbar": {
          width: "6px",
        },

        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#D1D5DB",
          borderRadius: "10px",
        },
      },
    },
  };

  return (
    <Box>
      {/* Label */}
      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 500,
          color: "var(--app-text-muted)",
          mb: "6px",
          fontFamily: "Figtree",
        }}
      >
        {label}
        {required && (
          <span style={{ color: "#d32f2f" }}>
            {" "}
            *
          </span>
        )}
      </Typography>

      <FormControl fullWidth>
        {/* RHF MODE */}
        {control ? (
          <Controller
            name={name}
            control={control}
            render={({ field }) =>
              searchable ? (
                <Autocomplete
                  options={options}
                  clearOnEscape
                  disableClearable={false}
                  disabled={disabled}
                  popupIcon={<KeyboardArrowDownIcon />}
                  sx={dropdownStyles}
                  getOptionLabel={(option) =>
                    option.label || ""
                  }
                  value={
                    options.find(
                      (opt) =>
                        opt.value === field.value
                    ) || null
                  }
                  onChange={(_, newValue) => {
                    field.onChange(
                      newValue?.value || ""
                    );
                  }}
                  isOptionEqualToValue={(
                    option,
                    value
                  ) =>
                    option.value === value.value
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={`Select ${label}`}
                      error={false}
                      sx={inputStyles}
                    />
                  )}
                />
              ) : (
                <Select
                  {...field}
                  disabled={disabled}
                  displayEmpty
                  IconComponent={
                    KeyboardArrowDownIcon
                  }
                  sx={selectStyles}
                  MenuProps={menuProps}
                >
                  {options.map((opt) => (
                    <MenuItem
                      key={opt.value}
                      value={opt.value}
                    >
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              )
            }
          />
        ) : searchable ? (
          <Autocomplete
            options={options}
            clearOnEscape
            disableClearable={false}
            disabled={disabled}
            popupIcon={<KeyboardArrowDownIcon />}
            sx={dropdownStyles}
            getOptionLabel={(option) =>
              option.label || ""
            }
            value={
              options.find(
                (opt) => opt.value === value
              ) || null
            }
            onChange={(_, newValue) => {
              onChange?.(
                newValue?.value || ""
              );
            }}
            isOptionEqualToValue={(
              option,
              value
            ) =>
              option.value === value.value
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={`Select ${label}`}
                error={false}
                sx={inputStyles}
              />
            )}
          />
        ) : (
          <Select
            displayEmpty
            disabled={disabled}
            value={value ?? ""}
            onChange={onChange}
             renderValue={(selected) => {
    if (!selected) {
      return (
        <span style={{ fontSize: "14px",
    fontWeight: 400,
    color: "var(--app-text-muted)",
    opacity: 1, }}>
          {placeholder || `Select ${label}`}
        </span>
      );
    }

    return selected;
  }}
            IconComponent={
              KeyboardArrowDownIcon
            }
            sx={selectStyles}
            MenuProps={menuProps}
          >
            {options.map((opt) => (
              <MenuItem
                key={opt.value}
                value={opt.value}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        )}

        {/* Error */}
        {fieldError?.message && (
          <Typography
            sx={{
              color: "#d32f2f",
              fontSize: "12px",
              mt: "4px",
              fontFamily: "Figtree",
            }}
          >
            {fieldError.message}
          </Typography>
        )}
      </FormControl>
    </Box>
  );
};

export default FormSelect;